import { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { StyledTableRow, StyledTableCell } from '@/components/ui/styled-table';
import { ApprovalCountsDisplay } from '@/components/shared/approval-counts-display';
import { VoteStatusBadge } from '@/components/shared/vote-status-badge';
import { getNetworkDisplayName } from '@mls-wallet/shared-types';
import { type ProposalWithSignatures } from '@/hooks/account/use-account-proposals';
import { getSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';
import { useAuthClient } from '@/lib/authClient';
import { PlusCircle } from 'lucide-react';

interface AccountProposalRowProps {
  proposal: ProposalWithSignatures;
  isSelected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
}

export function AccountProposalRow({
  proposal,
  isSelected,
  onSelectChange,
}: AccountProposalRowProps) {
  const { user } = useAuthClient();
  const { numApprovals, numRejections, userVote } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposal.approvals,
        rejections: proposal.rejections,
        userId: user?.id,
        requiredNetworkIds: proposal.proposal.networkIds,
      }),
    [
      proposal.approvals,
      proposal.rejections,
      user?.id,
      proposal.proposal.networkIds,
    ]
  );
  // Existing-account expansion proposals carry the account id they will update.
  // New-account proposals leave it null, so the plus icon becomes the review hint
  // that only the listed networks are being added.
  const isAddingNetworks = Boolean(proposal.proposal.multichainAccountId);

  return (
    <StyledTableRow
      className={`cursor-pointer transition-colors ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelectChange(proposal.proposal.id, !isSelected)}
    >
      <StyledTableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) =>
            onSelectChange(proposal.proposal.id, !!checked)
          }
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select proposal ${proposal.proposal.id}`}
        />
      </StyledTableCell>
      <StyledTableCell className="font-semibold text-base">
        {proposal.proposal.name}
      </StyledTableCell>
      <StyledTableCell>
        <div className="flex items-center gap-2 text-sm font-medium">
          {isAddingNetworks ? (
            <>
              <PlusCircle
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <span className="sr-only">Adding networks</span>
            </>
          ) : null}
          {proposal.proposal.networkIds
            .map((n) => getNetworkDisplayName(n))
            .join(', ')}
        </div>
      </StyledTableCell>
      <StyledTableCell>
        <ApprovalCountsDisplay
          approvals={numApprovals}
          rejections={numRejections}
          threshold={proposal.requiredApprovals}
        />
      </StyledTableCell>
      <StyledTableCell>
        <VoteStatusBadge userVote={userVote} />
      </StyledTableCell>
    </StyledTableRow>
  );
}
